// @ts-check

// CONTROL_ATTRIBUTE names, on a wrapper holding several controls, the one the picker opens.
export const CONTROL_ATTRIBUTE = "data-activator-control";

// FOCUSABLE treats `contenteditable` as the enumerated attribute it is: the bare and empty forms
// enable editing, only "false" disables it.
// What takes focus. `tabindex="-1"` takes anything out of the focus order whatever it otherwise is,
// so each disqualifier applies to every candidate rather than only to the branch it came from.
const IN_FOCUS_ORDER = ':not([tabindex="-1"])';
// A hidden input renders nothing, so it takes no focus however it is written — including with a
// `tabindex` of its own, which would otherwise match on the generic branch.
const RENDERED = ':not([type="hidden"])';
const FOCUSABLE = [
  "a[href]",
  "button",
  "input",
  "select",
  "textarea",
  "[tabindex]",
  '[contenteditable]:not([contenteditable="false"])',
]
  .map((candidate) => `${candidate}${IN_FOCUS_ORDER}${RENDERED}`)
  .join(",");

/**
 * Returns the children a wrapper holds, with a nested `<slot>` replaced by what it shows. A wrapper
 * rendering a slot of its own — `sp-select` and `FilterActivatorSlot` both do — holds the control
 * the consumer supplied, not the fallback the slot declares.
 *
 * @param {Element} element
 * @returns {Element[]}
 */
function slotContent(element) {
  return [...(element.children ?? [])].flatMap((child) =>
    child instanceof HTMLSlotElement ? child.assignedElements({ flatten: true }) : [child],
  );
}

/**
 * Returns every `<slot>` an activator renders, at any depth, so a change of what they show is a
 * change of control.
 *
 * @param {Element | null | undefined} element
 * @returns {HTMLSlotElement[]}
 */
export function activatorSlots(element) {
  if (!element) {
    return [];
  }

  const own = element instanceof HTMLSlotElement ? [element] : [];

  return [...own, .../** @type {HTMLSlotElement[]} */ ([...element.querySelectorAll("slot")])];
}

/**
 * Returns the control an activator element stands for — the node focus lands on, and the one the
 * accessibility tree reads. A disabled control still stands for the activator: the tree reports it
 * disabled rather than dropping the widget. Null when the element has none yet, and null when a
 * wrapper holds several and names none, since DOM order does not say which one opens the picker.
 *
 * A consumer may slot the control itself, or a wrapper holding it. An `sp-*` field renders its
 * control in a shadow root and hands it out (`sp-text-field` exposes `inputElement`), so the
 * element is asked rather than opened. A wrapper holding more than one names which child the
 * picker opens with `data-activator-control`.
 *
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
export function resolveActivatorControl(element) {
  if (!element) {
    return null;
  }

  // An element that renders its control in a shadow root hands it out: `sp-text-field` as
  // `inputElement`, `sp-button` as `controlElement`. Stating the property is the contract, so an
  // element that states one answers for itself while the control is still null.
  const exposed = /** @type {{ inputElement?: HTMLElement, controlElement?: HTMLElement }} */ (element);
  if ("inputElement" in exposed || "controlElement" in exposed) {
    return exposed.inputElement ?? exposed.controlElement ?? null;
  }

  if (element.matches?.(FOCUSABLE)) {
    return /** @type {HTMLElement} */ (element);
  }

  // A wrapper: the control is inside it, in light DOM. An `sp-*` child is asked in turn, since its
  // own control sits behind a boundary this must not open.
  const children = slotContent(element);
  const named = children.filter((child) => child.hasAttribute?.(CONTROL_ATTRIBUTE));
  const controls = (named.length > 0 ? named : children).map(resolveActivatorControl).filter(Boolean);

  return controls.length === 1 ? controls[0] : null;
}
